import { useGeckoLocale } from 'features/locale'
import React from 'react'
import styled from 'styled-components'
import {
  GeckoFormattingVariant,
  GeckoMeasurement,
  GeckoMeasurementRange,
} from 'types'

const StyledLabel = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const NameAndColon = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

/**
 * A component for showing a measurement as a "line item"- putting its string
 * name off to the left and its value off to the right
 * @param name            A name for this measurement
 * @param measurement     The measurement or measurement range to format
 * @param variant         The string variant to use (affects string length)
 * @param showLoading     Whether to show a "Loading..." filler string
 * @constructor
 */
export const GeckoMeasurementLineItem: React.FC<{
  name: React.ReactNode
  measurement: GeckoMeasurement | GeckoMeasurementRange | undefined
  variant?: GeckoFormattingVariant | 'none'
  showLoading?: boolean
}> = ({ name, measurement, showLoading, variant }) => {
  const { formatMeasurement, formatMeasurementRange } = useGeckoLocale()
  const measurementString = React.useMemo(
    () =>
      !measurement || measurement instanceof GeckoMeasurement
        ? formatMeasurement({
            measurement: measurement,
            showLoading: showLoading,
            variant: variant,
          })
        : formatMeasurementRange({
            measurement: measurement,
            showLoading: showLoading,
            variant: variant,
          }),
    [
      measurement,
      variant,
      showLoading,
      formatMeasurement,
      formatMeasurementRange,
    ]
  )
  return !measurement ? null : (
    <StyledLabel>
      <NameAndColon>
        {name}
        <span>: </span>
      </NameAndColon>
      <span>{measurementString}</span>
    </StyledLabel>
  )
}
